import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getToolName } from "ai";
import {
  getMessageText,
  getRenderableToolInput,
  getToolParts,
  HIDDEN_TOOL_NAMES,
} from "./messageUtils";

const MARGIN = 16;
const CONTENT_WIDTH = 178;
const LINE_HEIGHT = 4.8;

const COLORS = {
  primary: [37, 99, 235],
  text: [30, 41, 59],
  muted: [100, 116, 139],
  divider: [226, 232, 240],
  tableHead: [37, 99, 235],
  kpiBg: [239, 246, 255],
  kpiBorder: [147, 197, 253],
};

function getPageHeight(doc) {
  return doc.internal.pageSize.getHeight();
}

function ensureSpace(doc, y, needed = 20) {
  if (y + needed > getPageHeight(doc) - MARGIN) {
    doc.addPage();
    return MARGIN;
  }
  return y;
}

function formatCellValue(value) {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  return String(value);
}

function normalizeTableColumns(columns, rows) {
  if (Array.isArray(columns) && columns.length > 0) {
    return columns
      .map((column) => {
        if (typeof column === "string") return { key: column, label: column };
        if (!column || typeof column !== "object") return null;
        const key = column.key ?? column.id ?? column.field ?? column.name;
        if (!key) return null;
        return { key, label: column.label ?? column.title ?? key };
      })
      .filter(Boolean);
  }

  const firstObjectRow = (Array.isArray(rows) ? rows : []).find(
    (row) => row && typeof row === "object" && !Array.isArray(row)
  );
  return firstObjectRow
    ? Object.keys(firstObjectRow).map((key) => ({ key, label: key }))
    : [];
}

function pivotLongFormatData({ data, xKey, seriesKey, valueKey }) {
  const pivoted = new Map();

  data.forEach((row) => {
    const xVal = row[xKey];
    const seriesVal = String(row[seriesKey]);
    const existing = pivoted.get(xVal) ?? { [xKey]: xVal };
    existing[seriesVal] = row[valueKey];
    pivoted.set(xVal, existing);
  });

  return Array.from(pivoted.values());
}

function getChartTableData({
  data = [],
  x_key: xKey,
  series_key: seriesKey,
  value_key: valueKey,
}) {
  if (!Array.isArray(data) || data.length === 0) {
    return { columns: [], rows: [] };
  }

  let plotData = data;
  if (seriesKey && valueKey && xKey) {
    plotData = pivotLongFormatData({ data, xKey, seriesKey, valueKey });
  }

  const sampleRow = plotData[0] ?? {};
  const columns = Object.keys(sampleRow).map((key) => ({ key, label: key }));
  return { columns, rows: plotData };
}

function formatKpiValue(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(value ?? "");
}

function stripBasicMarkdown(content) {
  return String(content ?? "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/`([^`]+)`/g, "$1")
    .trim();
}

function addWrappedText(doc, text, y, options = {}) {
  const {
    fontSize = 10,
    fontStyle = "normal",
    color = COLORS.text,
    indent = 0,
    maxWidth = CONTENT_WIDTH - indent,
  } = options;

  if (!text) {
    return y;
  }

  doc.setFont("helvetica", fontStyle);
  doc.setFontSize(fontSize);
  doc.setTextColor(...color);
  const lines = doc.splitTextToSize(text, maxWidth);
  const blockHeight = lines.length * LINE_HEIGHT;
  y = ensureSpace(doc, y, blockHeight + 2);
  doc.text(lines, MARGIN + indent, y);
  return y + blockHeight;
}

function addSectionTitle(doc, y, title) {
  y = ensureSpace(doc, y, 10);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.text);
  doc.text(title, MARGIN, y);
  return y + 6;
}

function addTable(doc, y, { title, columns, rows }) {
  const normalizedColumns = normalizeTableColumns(columns, rows);
  if (!normalizedColumns.length) {
    return y;
  }

  if (title) {
    y = addSectionTitle(doc, y, title);
  }

  y = ensureSpace(doc, y, 24);
  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head: [normalizedColumns.map((column) => column.label ?? column.key)],
    body: (Array.isArray(rows) ? rows : []).map((row) =>
      normalizedColumns.map((column) => formatCellValue(row?.[column.key]))
    ),
    theme: "striped",
    styles: {
      fontSize: 8.5,
      cellPadding: 2.5,
      overflow: "linebreak",
      textColor: COLORS.text,
    },
    headStyles: {
      fillColor: COLORS.tableHead,
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
  });

  return (doc.lastAutoTable?.finalY ?? y) + 8;
}

function addKpiBlock(doc, y, { label, value, unit, delta, subtext }) {
  const formattedValue = `${unit ? `${unit} ` : ""}${formatKpiValue(value)}`;
  const lines = [formattedValue];
  if (delta !== undefined && delta !== null && delta !== "") {
    lines.push(String(delta));
  }
  if (subtext) {
    lines.push(subtext);
  }

  const boxHeight = 14 + lines.length * 5;
  y = ensureSpace(doc, y, boxHeight + 4);

  doc.setFillColor(...COLORS.kpiBg);
  doc.setDrawColor(...COLORS.kpiBorder);
  doc.roundedRect(MARGIN, y - 3, CONTENT_WIDTH, boxHeight, 2, 2, "FD");

  if (label) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.muted);
    doc.text(label, MARGIN + 4, y + 3);
    y += 6;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.primary);
  doc.text(formattedValue, MARGIN + 4, y + 4);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.muted);

  if (delta !== undefined && delta !== null && delta !== "") {
    doc.text(String(delta), MARGIN + 4, y + 2);
    y += 5;
  }
  if (subtext) {
    const subtextLines = doc.splitTextToSize(subtext, CONTENT_WIDTH - 8);
    doc.text(subtextLines, MARGIN + 4, y + 2);
    y += subtextLines.length * 4.5;
  }

  return y + 8;
}

function addMessageDivider(doc, y) {
  y = ensureSpace(doc, y, 8);
  doc.setDrawColor(...COLORS.divider);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, y, MARGIN + CONTENT_WIDTH, y);
  return y + 8;
}

function addRoleHeader(doc, y, role) {
  const isAssistant = role === "assistant";
  y = ensureSpace(doc, y, 10);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...(isAssistant ? COLORS.primary : COLORS.muted));
  doc.text(isAssistant ? "Amy" : "You", MARGIN, y);
  return y + 7;
}

function getToolExportBlocks(part) {
  const toolName = getToolName(part);
  const input = getRenderableToolInput(part);
  if (!input || HIDDEN_TOOL_NAMES.has(toolName)) {
    return [];
  }

  if (toolName === "render_table") {
    return [{ type: "table", title: input.title || "Table", columns: input.columns, rows: input.rows }];
  }

  if (toolName === "render_chart") {
    const { columns, rows } = getChartTableData(input);
    if (!columns.length) {
      return [];
    }
    return [
      {
        type: "table",
        title: input.title || `${input.chart_type || "Chart"} data`,
        columns,
        rows,
      },
    ];
  }

  if (toolName === "render_kpi") {
    return [{ type: "kpi", ...input }];
  }

  if (toolName === "render_markdown" && input.content) {
    return [{ type: "text", content: stripBasicMarkdown(input.content) }];
  }

  return [];
}

function addDocumentHeader(doc, { claimId }) {
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, 210, 28, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(255, 255, 255);
  doc.text("Amy Chat Export", MARGIN, 14);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Exported ${new Date().toLocaleString()}`, MARGIN, 22);

  let y = 36;
  if (claimId) {
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.muted);
    doc.text(`Claim ID: ${claimId}`, MARGIN, y);
    y += 8;
  }

  return y + 4;
}

function buildChatPdf(messages, { claimId } = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = addDocumentHeader(doc, { claimId });

  (Array.isArray(messages) ? messages : []).forEach((message, index) => {
    const text = getMessageText(message).trim();
    const toolBlocks = getToolParts(message).flatMap(getToolExportBlocks);

    if (!text && toolBlocks.length === 0) {
      return;
    }

    y = addRoleHeader(doc, y, message.role);

    if (text) {
      y = addWrappedText(doc, text, y, { fontSize: 10, lineHeight: LINE_HEIGHT });
      y += 3;
    }

    toolBlocks.forEach((block) => {
      if (block.type === "table") {
        y = addTable(doc, y, block);
      } else if (block.type === "kpi") {
        y = addKpiBlock(doc, y, block);
      } else if (block.type === "text") {
        y = addWrappedText(doc, block.content, y, { fontSize: 9.5, color: COLORS.muted });
        y += 3;
      }
    });

    if (index < messages.length - 1) {
      y = addMessageDivider(doc, y);
    }
  });

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.muted);
    doc.text(`Page ${page} of ${pageCount}`, 210 - MARGIN, getPageHeight(doc) - 8, { align: "right" });
  }

  return doc;
}

export function downloadChat(messages, { claimId } = {}) {
  if (!Array.isArray(messages) || messages.length === 0) {
    return;
  }

  const doc = buildChatPdf(messages, { claimId });
  const date = new Date().toISOString().slice(0, 10);
  const claimSuffix = claimId ? `-claim-${claimId}` : "";
  doc.save(`amy-chat${claimSuffix}-${date}.pdf`);
}
